import type { CssKeyframe } from './pose';
import { paletteColor } from './palette';
import type { Label, PresetParam } from './presets';
import type { FxAnim, FxContext } from './fx';

/**
 * Shared helpers of the motion graphics kinds (fx.ts, fx-reel.ts): units, parameters, palette
 * colours, cover timing and rough text layout.
 */

export const r2 = (v: number) => Math.round(v * 100) / 100;
export const px = (v: number) => `${r2(v)}px`;

/** Expressive in-out curve for covers; out curve for arrivals. */
export const IO = 'cubic-bezier(0.83, 0, 0.17, 1)';
export const OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';
export const IN = 'cubic-bezier(0.7, 0, 0.84, 0)';
export const BACK = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

export function rng(seed: number) {
  let s = seed >>> 0 || 1;
  return () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296;
}

export const unit = (ctx: FxContext) => Math.min(ctx.canvas.width, ctx.canvas.height) / 1080;
export const num = (ctx: FxContext, key: string, d: number) => {
  const v = Number(ctx.params[key]);
  return Number.isFinite(v) ? v : d;
};
export const str = (ctx: FxContext, key: string, d: string) =>
  typeof ctx.params[key] === 'string' && ctx.params[key] ? (ctx.params[key] as string) : d;
export const color = (
  ctx: FxContext,
  key: string,
  d: 'accent' | 'accent2' | 'dark' | 'light' | 'ink',
) => paletteColor(ctx.params[key], ctx.palette, d);

export const full = (ctx: FxContext, extra: Record<string, string | number> = {}) => ({
  position: 'absolute',
  left: '0px',
  top: '0px',
  width: px(ctx.canvas.width),
  height: px(ctx.canvas.height),
  ...extra,
});

export type Mode = 'through' | 'cover' | 'reveal';
export const modeOf = (ctx: FxContext): Mode => {
  const m = ctx.params.mode;
  return m === 'cover' || m === 'reveal' ? m : 'through';
};

/**
 * One node of a cover effect: comes in from `from`, rests, leaves to `to`. In "through" mode the
 * screen is fully covered between 45 % and 55 % of the item (transitions swap scenes at 50 %).
 */
export function coverAnim(
  ctx: FxContext,
  i: number,
  n: number,
  j: number,
  from: CssKeyframe,
  rest: CssKeyframe,
  to: CssKeyframe,
  stagger = 0.16,
  easing = IO,
): FxAnim {
  const D = ctx.duration;
  const mode = modeOf(ctx);
  if (mode === 'through') {
    const E = D * 0.45;
    const st = n > 1 ? Math.min((E * 0.5) / (n - 1), E * stagger) : 0;
    const e = E - (n - 1) * st;
    const start = i * st;
    const exitStart = D * 0.55 + j * st;
    const dur = exitStart + e - start;
    return {
      delay: start,
      duration: dur,
      fill: 'both',
      keyframes: [
        { ...from, offset: 0, easing },
        { ...rest, offset: e / dur },
        { ...rest, offset: (exitStart - start) / dur, easing },
        { ...to, offset: 1 },
      ],
    };
  }
  const st = n > 1 ? Math.min((D * 0.4) / (n - 1), D * stagger) : 0;
  const e = D - (n - 1) * st;
  if (mode === 'cover')
    return {
      delay: i * st,
      duration: e,
      fill: 'both',
      keyframes: [{ ...from, easing }, { ...rest }],
    };
  return {
    delay: j * st,
    duration: e,
    fill: 'both',
    keyframes: [{ ...rest, easing }, { ...to }],
  };
}

export const lbl = (
  en: string,
  uk: string,
  pl: string,
  ro: string,
  es: string,
  ru: string,
): Label => [en, uk, pl, ro, es, ru];

export const P_MODE: PresetParam = {
  key: 'mode',
  type: 'select',
  default: 'through',
  label: lbl('Mode', 'Режим', 'Tryb', 'Mod', 'Modo', 'Режим'),
  options: [
    {
      value: 'through',
      label: lbl(
        'Cover and reveal',
        'Закрити й відкрити',
        'Zakryj i odkryj',
        'Acoperă și dezvăluie',
        'Cubrir y revelar',
        'Закрыть и открыть',
      ),
    },
    {
      value: 'reveal',
      label: lbl('Reveal', 'Відкрити', 'Odkryj', 'Dezvăluie', 'Revelar', 'Открыть'),
    },
    { value: 'cover', label: lbl('Cover', 'Закрити', 'Zakryj', 'Acoperă', 'Cubrir', 'Закрыть') },
  ],
};
export const P_DIR: PresetParam = {
  key: 'direction',
  type: 'direction',
  default: 'right',
  label: lbl('Direction', 'Напрямок', 'Kierunek', 'Direcție', 'Dirección', 'Направление'),
};
export const P_COUNT = (d: number, min = 1, max = 12): PresetParam => ({
  key: 'count',
  type: 'number',
  default: d,
  min,
  max,
  label: lbl('Count', 'Кількість', 'Liczba', 'Număr', 'Cantidad', 'Количество'),
});
export const P_COLOR = (key: string, d: string, l?: Label): PresetParam => ({
  key,
  type: 'color',
  default: d,
  label: l ?? lbl('Colour', 'Колір', 'Kolor', 'Culoare', 'Color', 'Цвет'),
});
export const P_COLOR2 = (d: string) =>
  P_COLOR(
    'color2',
    d,
    lbl(
      'Second colour',
      'Другий колір',
      'Drugi kolor',
      'A doua culoare',
      'Segundo color',
      'Второй цвет',
    ),
  );
export const P_COLOR3 = (d: string) =>
  P_COLOR(
    'color3',
    d,
    lbl(
      'Third colour',
      'Третій колір',
      'Trzeci kolor',
      'A treia culoare',
      'Tercer color',
      'Третий цвет',
    ),
  );
export const P_X: PresetParam = {
  key: 'x',
  type: 'number',
  default: 50,
  min: 0,
  max: 100,
  label: lbl(
    'Centre X, %',
    'Центр X, %',
    'Środek X, %',
    'Centru X, %',
    'Centro X, %',
    'Центр X, %',
  ),
};
export const P_Y: PresetParam = {
  key: 'y',
  type: 'number',
  default: 50,
  min: 0,
  max: 100,
  label: lbl(
    'Centre Y, %',
    'Центр Y, %',
    'Środek Y, %',
    'Centru Y, %',
    'Centro Y, %',
    'Центр Y, %',
  ),
};
export const P_TEXT: PresetParam = {
  key: 'text',
  type: 'text',
  default: '',
  label: lbl(
    'Text (empty = headline)',
    'Текст (порожньо = заголовок)',
    'Tekst (pusty = nagłówek)',
    'Text (gol = titlu)',
    'Texto (vacío = título)',
    'Текст (пусто = заголовок)',
  ),
};
export const P_TARGET: PresetParam = {
  key: 'target',
  type: 'layer',
  default: '',
  label: lbl('Layer', 'Шар', 'Warstwa', 'Strat', 'Capa', 'Слой'),
};
export const P_OPACITY = (d: number): PresetParam => ({
  key: 'opacity',
  type: 'number',
  default: d,
  min: 0.05,
  max: 1,
  step: 0.05,
  label: lbl('Opacity', 'Непрозорість', 'Krycie', 'Opacitate', 'Opacidad', 'Непрозрачность'),
});

export type Dir = 'up' | 'down' | 'left' | 'right';
export const dirOf = (ctx: FxContext, d: Dir = 'right'): Dir => {
  const v = ctx.params.direction;
  return v === 'up' || v === 'down' || v === 'left' || v === 'right' ? v : d;
};
/** translate() for a canvas-sized node fully off-canvas against (k = -1) or along (k = 1) d. */
export function off(d: Dir, k: number): string {
  const v = `${101 * k}%`;
  return d === 'left' || d === 'right'
    ? `translate(${d === 'right' ? v : `${-101 * k}%`}, 0%)`
    : `translate(0%, ${d === 'down' ? v : `${-101 * k}%`})`;
}

export function palette3(
  ctx: FxContext,
  d1: 'accent' | 'accent2' | 'dark' | 'light',
  d2: 'accent' | 'accent2' | 'dark' | 'light',
  d3: 'accent' | 'accent2' | 'dark' | 'light',
) {
  return [color(ctx, 'color', d1), color(ctx, 'color2', d2), color(ctx, 'color3', d3)];
}

/** Rough text layout: lines of `words` at font size f within width w (average glyph 0.6 em). */
export function layoutLines(words: string[], f: number, w: number, upper: boolean): string[][] {
  const em = upper ? 0.68 : 0.58;
  const lines: string[][] = [[]];
  let cur = 0;
  for (const word of words) {
    const ww = [...word].length * em * f;
    const gap = lines[lines.length - 1].length ? 0.3 * f : 0;
    if (cur + gap + ww > w && lines[lines.length - 1].length) {
      lines.push([word]);
      cur = ww;
    } else {
      lines[lines.length - 1].push(word);
      cur += gap + ww;
    }
  }
  return lines;
}

/** Lines of even width (a title never ends with one short word on its own line). */
export function balancedLines(words: string[], f: number, w: number, upper: boolean): string[][] {
  const greedy = layoutLines(words, f, w, upper);
  if (greedy.length < 2) return greedy;
  const em = upper ? 0.68 : 0.58;
  const width = (x: string) => [...x].length * em * f;
  const total = words.reduce((sum, x) => sum + width(x), 0) + (words.length - 1) * 0.3 * f;
  const target = Math.min(w, (total / greedy.length) * 1.12);
  const balanced = layoutLines(words, f, target, upper);
  return balanced.length === greedy.length ? balanced : greedy;
}

/** Largest font size at which the words fit the box. */
export function fitFont(
  words: string[],
  w: number,
  h: number,
  upper: boolean,
  max: number,
): number {
  const em = upper ? 0.68 : 0.58;
  let f = max;
  while (f > 8) {
    const longest = Math.max(...words.map((x) => [...x].length)) * em * f;
    const lines = layoutLines(words, f, w, upper).length;
    if (longest <= w && lines * f * 1.08 <= h) return f;
    f *= 0.92;
  }
  return 8;
}

export function headlineWords(ctx: FxContext, maxWords = 8): string[] {
  const t = str(ctx, 'text', ctx.text || 'Hello')
    .replace(/\s+/g, ' ')
    .trim();
  // Typographic glue: a dash stays with the word before it, a short word with the word after it
  const words: string[] = [];
  let carry = '';
  for (const w of t.split(' ').filter(Boolean)) {
    if (/^[—–-]+$/.test(w) && words.length) {
      words[words.length - 1] += '\u00a0' + w;
    } else if ([...w].length <= 2 && /\p{L}/u.test(w)) {
      carry += w + '\u00a0';
    } else {
      words.push(carry + w);
      carry = '';
    }
  }
  if (carry) {
    if (words.length) words[words.length - 1] += '\u00a0' + carry.trim();
    else words.push(carry.trim());
  }
  return words.length > maxWords
    ? [...words.slice(0, maxWords - 1), words.slice(maxWords - 1).join(' ')]
    : words;
}

/** Text effects: a text layer whose text, font and colour the effect takes (and hides). */
export const P_SOURCE: PresetParam = {
  key: 'source',
  type: 'layer',
  default: '',
  label: lbl(
    'Text layer (text, font, colour)',
    'Текстовий шар (текст, шрифт, колір)',
    'Warstwa tekstu (tekst, czcionka, kolor)',
    'Strat de text (text, font, culoare)',
    'Capa de texto (texto, fuente, color)',
    'Текстовый слой (текст, шрифт, цвет)',
  ),
};
export const P_HIDE_SOURCE: PresetParam = {
  key: 'hideSource',
  type: 'select',
  default: 'on',
  label: lbl(
    'Hide the layer meanwhile',
    'Ховати шар під час ефекту',
    'Ukryj warstwę w trakcie',
    'Ascunde stratul între timp',
    'Ocultar la capa mientras',
    'Скрывать слой на время',
  ),
  options: [
    { value: 'on', label: lbl('Yes', 'Так', 'Tak', 'Da', 'Sí', 'Да') },
    { value: 'off', label: lbl('No', 'Ні', 'Nie', 'Nu', 'No', 'Нет') },
  ],
};
export const P_FONT: PresetParam = {
  key: 'font',
  type: 'font',
  default: '',
  label: lbl('Font', 'Шрифт', 'Czcionka', 'Font', 'Fuente', 'Шрифт'),
};
export const P_WEIGHT: PresetParam = {
  key: 'weight',
  type: 'select',
  default: '',
  label: lbl('Weight', 'Насиченість', 'Grubość', 'Grosime', 'Grosor', 'Насыщенность'),
  options: [
    {
      value: '',
      label: lbl(
        'As the font',
        'Як у шрифту',
        'Jak czcionka',
        'Ca fontul',
        'Como la fuente',
        'Как у шрифта',
      ),
    },
    ...[300, 400, 500, 600, 700, 800, 900].map((w) => ({
      value: String(w),
      label: lbl(String(w), String(w), String(w), String(w), String(w), String(w)),
    })),
  ],
};
